import { Fragment, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  assignAppRole,
  blockUser,
  getTestReports,
  getUsers,
  revokeAppRole,
} from '../api/admin.js';
import type { TestReport } from '@m8-app/contracts/rest/test-reports.js';

const TESTER_ROLE = 'production_test';

export function ProductionTesters() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['testers'],
    queryFn: () => getUsers({ role: TESTER_ROLE }),
  });

  const { mutate: toggleBlock } = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
      blockUser(id, { blocked }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['testers'] }),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (userId: string) => revokeAppRole(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['testers'] }),
  });

  const all = data?.users ?? [];
  const testers = all.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.mobile.includes(s);
  });

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Production Testers</h2>
          <p className="text-sm text-gray-500">
            Factory QA accounts. A tester logs into the mobile app&apos;s Test Mode to exercise
            every function of a freshly built controller, then submits a pass/fail report.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <span className="text-lg leading-none">+</span> Add Tester
        </button>
      </div>

      <div className="relative mb-4 max-w-md">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">⌕</span>
        <input
          type="text"
          placeholder="Search name or mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Mobile</th>
              <th className="px-4 py-3 font-medium">Added</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {testers.map((u) => {
              const isBlocked = u.status === 'blocked';
              const isOpen = expanded === u.id;
              return (
                <Fragment key={u.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{u.name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{u.mobile}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isBlocked
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-green-50 text-green-700 border border-green-200'
                      }`}>
                        {isBlocked ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => setExpanded(isOpen ? null : u.id)}
                        className="text-xs px-3 py-1 rounded-full font-medium border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                      >
                        {isOpen ? 'Hide reports' : 'Reports'}
                      </button>
                      <button
                        onClick={() => toggleBlock({ id: u.id, blocked: !isBlocked })}
                        className={`text-xs px-3 py-1 rounded-full font-medium border transition-colors ${
                          isBlocked
                            ? 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                            : 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'
                        }`}
                      >
                        {isBlocked ? '✓ Unblock' : '⊘ Block'}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Remove tester ${u.name || u.mobile}? They will lose Test Mode access.`)) {
                            remove(u.id);
                          }
                        }}
                        className="text-xs px-3 py-1 rounded-full font-medium border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 bg-gray-50">
                        <TesterReports testerId={u.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {testers.length === 0 && !isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No testers yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && <AddTesterDialog onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function TesterReports({ testerId }: { testerId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['testReports', testerId],
    queryFn: () => getTestReports({ testerId }),
  });

  if (isLoading) return <p className="text-xs text-gray-400">Loading reports…</p>;
  const reports = data?.reports ?? [];
  if (reports.length === 0) return <p className="text-xs text-gray-400">No reports submitted yet.</p>;

  return (
    <div className="space-y-2">
      {reports.map((r: TestReport) => (
        <div key={r.id} className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${
                r.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                {r.passed ? 'PASS' : 'FAIL'}
              </span>
              <span className="font-mono text-gray-700">{r.deviceSerial}</span>
              <span className="text-gray-400">· {r.deviceKind}</span>
            </div>
            <span className="text-xs text-gray-400">{formatDateTime(r.createdAt)}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {r.results.map((step) => (
              <span
                key={step.step}
                className={`text-[11px] px-2 py-0.5 rounded border ${
                  step.passed
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}
                title={step.passed ? 'Passed' : 'Failed'}
              >
                {step.passed ? '✓' : '✕'} {step.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AddTesterDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      assignAppRole({ mobile: mobile.trim(), role: TESTER_ROLE, displayName: name.trim() || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['testers'] });
      onClose();
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg ?? 'Failed to add tester');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutate();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Add Production Tester</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="pt-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              id="pt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tester name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="pt-mobile" className="block text-sm font-medium text-gray-700 mb-1">Mobile number</label>
            <input
              id="pt-mobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+91…"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">
              The tester logs into the app with this number; Test Mode opens automatically.
            </p>
          </div>
          {error && <div className="text-sm bg-red-50 text-red-700 rounded-lg px-3 py-2">{error}</div>}
          <button
            type="submit"
            disabled={isPending}
            className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {isPending ? 'Adding…' : 'Add Tester'}
          </button>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
